import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Card, Col, Dropdown, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { outcomeBadge, thaiDate, todayISO } from './nurseOptions';
import ThaiDateInput from '../components/ThaiDateInput'

const THAI_MONTH = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const ymLabel = (ym) => {
  const [y, m] = String(ym).split('-');
  return `${THAI_MONTH[Number(m)]} ${String(Number(y) + 543).slice(2)}`;
};

const Stat = ({ label, value, variant = '', hint }) => (
  <Card className="card-one h-100"><Card.Body className="p-3">
    <div className="fs-xs text-secondary">{label}</div>
    <h3 className={`card-value mb-0 ${variant}`}>{Number(value || 0)}</h3>
    {hint && <div className="fs-xs text-secondary">{hint}</div>}
  </Card.Body></Card>
);

// แถบสัดส่วนแบบง่าย — ตารางตัวเลขล้วนอ่านยากเวลาต้องหาว่า "ห้องไหนเยอะสุด"
const Bar = ({ value, max, variant = 'primary' }) => (
  <div className="progress" style={{ height: 6 }}>
    <div className={`progress-bar bg-${variant}`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
  </div>
);

export default function NurseReports() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [from, setFrom] = useState(`${todayISO().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`/api/nurse/reports?yearId=${yearId}&from=${from}&to=${to}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [yearId, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = data?.summary || {};
  const maxSymptom = Math.max(1, ...(data?.symptoms || []).map(x => Number(x.visits)));
  const maxRoom = Math.max(1, ...(data?.byRoom || []).map(x => Number(x.visits)));
  const maxTrend = Math.max(1, ...(data?.trend || []).map(x => Number(x.visits)));
  const outbreaks = [...(data?.outbreak?.rooms || []), ...(data?.outbreak?.dorms || [])];

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">รายงาน</li>
            </ol>
            <h4 className="main-title mb-0">รายงานห้องพยาบาล</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <ThaiDateInput size="sm" style={{ maxWidth: 150 }} value={from}
              onChange={v => setFrom(v)} />
            <ThaiDateInput size="sm" style={{ maxWidth: 150 }} value={to}
              onChange={v => setTo(v)} />
            <Dropdown>
              <Dropdown.Toggle variant="primary" size="sm" id="nurse-report-year">
                {yearLabel || 'ปีการศึกษา'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(y => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {loading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}

        {outbreaks.length > 0 && (
          <div className="alert alert-danger py-2 mb-3">
            <div className="fw-semibold mb-1">
              <i className="ri-alarm-warning-line me-1"></i>สัญญาณการระบาดใน 7 วันล่าสุด
            </div>
            <ul className="mb-0 ps-3 fs-sm">
              {(data.outbreak.rooms || []).map((r, i) => (
                <li key={`r${i}`}>ม.{r.c_id}/{r.r_no} — {r.symptoms} <b>{r.students} คน</b> (ล่าสุด {thaiDate(r.last_date)})</li>
              ))}
              {(data.outbreak.dorms || []).map((d, i) => (
                <li key={`d${i}`}>หอ {d.dorm_name} — {d.symptoms} <b>{d.students} คน</b> (นับเฉพาะผู้มีทะเบียนหอพัก)</li>
              ))}
            </ul>
          </div>
        )}

        <Row className="g-3 mb-3">
          <Col xs={6} lg={3}><Stat label="มาห้องพยาบาล" value={s.visits} hint={`${Number(s.students || 0)} คน`} /></Col>
          <Col xs={6} lg={3}><Stat label="นอนพัก" value={s.rested} variant="text-warning" /></Col>
          <Col xs={6} lg={3}><Stat label="ส่งโรงพยาบาล" value={s.referred} variant="text-danger" /></Col>
          <Col xs={6} lg={3}><Stat label="เกิดตอนกลางคืน/หอพัก" value={s.at_night} hint="บันทึกโดยเวรหอพัก" /></Col>
        </Row>

        <Row className="g-3">
          <Col xl={6}>
            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6" className="mb-0">อาการที่พบ</Card.Title></Card.Header>
              <Card.Body className="p-0">
                {!data?.symptoms?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีข้อมูลในช่วงที่เลือก</div>
                ) : (
                  <Table className="mb-0 fs-sm">
                    <thead><tr><th>อาการ</th><th style={{ width: 130 }}>สัดส่วน</th><th className="text-end">ครั้ง</th><th className="text-end">คน</th></tr></thead>
                    <tbody>
                      {data.symptoms.map(x => (
                        <tr key={x.ns_id}>
                          <td>
                            {x.ns_name}
                            {x.ns_watch ? <Badge bg="danger" className="ms-1 fw-normal">เฝ้าระวัง</Badge> : null}
                          </td>
                          <td><Bar value={Number(x.visits)} max={maxSymptom} variant={x.ns_watch ? 'danger' : 'primary'} /></td>
                          <td className="text-end fw-semibold">{x.visits}</td>
                          <td className="text-end text-secondary">{x.students}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6" className="mb-0">แนวโน้มรายเดือน (ทั้งปีการศึกษา)</Card.Title></Card.Header>
              <Card.Body className="p-0">
                {!data?.trend?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีข้อมูล</div>
                ) : (
                  <Table className="mb-0 fs-sm">
                    <thead><tr><th>เดือน</th><th style={{ width: 140 }}></th><th className="text-end">ครั้ง</th><th className="text-end">ส่ง รพ.</th></tr></thead>
                    <tbody>
                      {data.trend.map(t => (
                        <tr key={t.ym}>
                          <td>{ymLabel(t.ym)}</td>
                          <td><Bar value={Number(t.visits)} max={maxTrend} /></td>
                          <td className="text-end fw-semibold">{t.visits}</td>
                          <td className="text-end text-danger">{Number(t.referred || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">ยาที่ใช้มากที่สุด</Card.Title></Card.Header>
              <Card.Body className="p-0">
                {!data?.medicines?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ยังไม่มีการจ่ายยาในช่วงนี้</div>
                ) : (
                  <Table className="mb-0 fs-sm">
                    <thead><tr><th>ยา</th><th className="text-end">จำนวน</th><th className="text-end">ครั้งที่จ่าย</th></tr></thead>
                    <tbody>
                      {data.medicines.map(m => (
                        <tr key={m.mi_id}>
                          <td>{m.mi_name}</td>
                          <td className="text-end fw-semibold">{Number(m.qty)} {m.mi_unit}</td>
                          <td className="text-end text-secondary">{m.visits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6" className="mb-0">รายห้องเรียน</Card.Title></Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: 340, overflowY: 'auto' }}>
                {!data?.byRoom?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีข้อมูล</div>
                ) : (
                  <Table className="mb-0 fs-sm">
                    <thead><tr><th>ห้อง</th><th style={{ width: 120 }}></th><th className="text-end">ครั้ง</th><th className="text-end">ขาดเรียน</th></tr></thead>
                    <tbody>
                      {data.byRoom.map(r => (
                        <tr key={r.r_id}>
                          <td>ม.{r.c_id}/{r.r_no}</td>
                          <td><Bar value={Number(r.visits)} max={maxRoom} /></td>
                          <td className="text-end fw-semibold">{r.visits}</td>
                          <td className="text-end text-warning">{Number(r.absent_causing || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one mb-3">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">รายหอพัก</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {!data?.byDorm?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีข้อมูล</div>
                ) : (
                  <>
                    <Table className="mb-0 fs-sm">
                      <thead><tr><th>หอพัก</th><th>เขต</th><th className="text-end">ครั้ง</th><th className="text-end">คน</th></tr></thead>
                      <tbody>
                        {data.byDorm.map(d => (
                          <tr key={d.dorm_id}>
                            <td>{d.dorm_name}</td>
                            <td className="fs-xs text-secondary">{d.village_name}</td>
                            <td className="text-end fw-semibold">{d.visits}</td>
                            <td className="text-end text-secondary">{d.students}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <div className="fs-xs text-secondary p-2 border-top">
                      นับเฉพาะนักเรียนที่มีทะเบียนหอพัก — คนที่ไม่มีทะเบียนไม่ได้แปลว่าไม่ได้พักหอ
                      ตัวเลขนี้จึงอ่านได้แค่ว่า “พบอย่างน้อยเท่านี้” ไม่ใช่อัตราต่อหลัง
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6" className="mb-0">มาบ่อยผิดปกติ</Card.Title></Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: 300, overflowY: 'auto' }}>
                {!data?.frequent?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีนักเรียนที่เข้าเกณฑ์</div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <tbody>
                      {data.frequent.map(f => (
                        <tr key={f.st_id}>
                          <td>
                            <Link to={`/nurse/students/${f.st_id}`} className="fw-semibold">{f.st_name}</Link>
                            <div className="fs-xs text-secondary">ม.{f.c_id}/{f.r_no} · {f.symptoms}</div>
                          </td>
                          <td className="text-end align-middle" style={{ width: 80 }}>
                            <Badge bg="danger">{f.visits} ครั้ง</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">การส่งต่อ / ส่งกลับบ้าน</Card.Title></Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: 340, overflowY: 'auto' }}>
                {!data?.referrals?.length ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีการส่งต่อในช่วงนี้</div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <tbody>
                      {data.referrals.map(r => (
                        <tr key={r.nv_id}>
                          <td className="text-nowrap fs-xs">{thaiDate(r.nv_date)}</td>
                          <td>
                            <Link to={`/nurse/students/${r.st_id}`}>{r.st_name}</Link>
                            <div className="fs-xs text-secondary">
                              ม.{r.c_id}/{r.r_no} · {r.ns_name || '-'}
                              {r.nv_refer_place ? ` · ${r.nv_refer_place}` : ''}
                            </div>
                          </td>
                          <td className="text-end align-middle">{outcomeBadge(r.nv_outcome)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
